<template>
  <div class="vms-visits-page">
    <VmsPageHeader
      kicker="Visits"
      :title="visitsPageTitle"
      subtitle="Invite a guest, approve waiting requests, or review today’s schedule — tap a card to filter."
      icon="mdi-calendar-account"
    >
      <template #actions>
        <v-text-field
          v-model="filterReportBySearch"
          append-icon="mdi-magnify"
          label="Search guest, host, title…"
          dense
          outlined
          hide-details
          clearable
          class="vms-toolbar-search"
          @keyup.enter="runSmartSearch"
        ></v-text-field>
        <v-btn
          small
          outlined
          color="info"
          class="text-capitalize"
          :loading="smartSearchLoading"
          @click="runSmartSearch"
        >
          <v-icon left small>mdi-creation</v-icon>
          Smart search
        </v-btn>
        <v-btn-toggle v-model="listMode" mandatory dense class="vms-mode-toggle">
          <v-btn small value="upcoming" @click="showUpcomingVisit">
            <v-icon left small>mdi-calendar-end</v-icon> Today
          </v-btn>
          <v-btn small value="past" @click="showPastVisit">
            <v-icon left small>mdi-history</v-icon> Past
          </v-btn>
        </v-btn-toggle>
        <v-btn color="info" dark @click="addVisit" class="vms-primary-cta">
          <v-icon left>mdi-calendar-plus</v-icon>
          Invite visitor
        </v-btn>
      </template>
    </VmsPageHeader>

    <div class="vms-snapshot" v-if="showUpcomingVisits">
      <div
        class="vms-snapshot__card"
        :class="{ 'is-active': statusFilter === 'all' }"
        @click="applyStatusFilter('all')"
      >
        <div class="vms-snapshot__label">All today</div>
        <div class="vms-snapshot__value">{{ snapshot.all }}</div>
      </div>
      <div
        class="vms-snapshot__card"
        :class="{ 'is-active': statusFilter === 'mine' }"
        @click="applyStatusFilter('mine')"
      >
        <div class="vms-snapshot__label">Needs my approval</div>
        <div class="vms-snapshot__value info--text">{{ snapshot.mine }}</div>
      </div>
      <div
        class="vms-snapshot__card"
        :class="{ 'is-active': statusFilter === 'pending' }"
        @click="applyStatusFilter('pending')"
      >
        <div class="vms-snapshot__label">Pending</div>
        <div class="vms-snapshot__value">{{ snapshot.pending }}</div>
      </div>
      <div
        class="vms-snapshot__card"
        :class="{ 'is-active': statusFilter === 'approved' }"
        @click="applyStatusFilter('approved')"
      >
        <div class="vms-snapshot__label">Approved</div>
        <div class="vms-snapshot__value success--text">{{ snapshot.approved }}</div>
      </div>
    </div>

    <div class="vms-filter-row" v-if="showUpcomingVisits">
      <v-alert
        v-if="smartSearchHint"
        dense
        text
        type="info"
        class="mb-0 mr-2"
        border="left"
        dismissible
        @input="(v) => { if (v === false) smartSearchHint = '' }"
      >
        {{ smartSearchHint }}
      </v-alert>
      <v-chip
        small
        :outlined="statusFilter !== 'all'"
        :color="statusFilter === 'all' ? 'info' : undefined"
        :dark="statusFilter === 'all'"
        @click="applyStatusFilter('all')"
      >All</v-chip>
      <v-chip
        small
        :outlined="statusFilter !== 'mine'"
        :color="statusFilter === 'mine' ? 'info' : undefined"
        :dark="statusFilter === 'mine'"
        @click="applyStatusFilter('mine')"
      >
        <v-icon left x-small>mdi-account-check</v-icon>
        Needs my approval
      </v-chip>
      <v-chip
        small
        :outlined="statusFilter !== 'pending'"
        :color="statusFilter === 'pending' ? 'warning' : undefined"
        :dark="statusFilter === 'pending'"
        @click="applyStatusFilter('pending')"
      >Pending</v-chip>
      <v-chip
        small
        :outlined="statusFilter !== 'approved'"
        :color="statusFilter === 'approved' ? 'success' : undefined"
        :dark="statusFilter === 'approved'"
        @click="applyStatusFilter('approved')"
      >Approved</v-chip>
      <v-chip
        small
        :outlined="statusFilter !== 'rejected'"
        :color="statusFilter === 'rejected' ? 'error' : undefined"
        :dark="statusFilter === 'rejected'"
        @click="applyStatusFilter('rejected')"
      >Rejected</v-chip>
      <v-spacer></v-spacer>
      <v-select
        v-model="selectedHeaders"
        :items="visitHeaders"
        label="Columns"
        multiple
        outlined
        return-object
        dense
        hide-details
        hide-selected
        style="max-width: 200px"
        @change="toggleColumnVisibility"
      >
        <template v-slot:selection="{ index }">
          <span v-if="index === 0" class="caption grey--text">{{ selectedHeaders.length }} shown</span>
        </template>
      </v-select>
    </div>

    <VmsResponsiveList :items="filteredVisits" :loading="loading" loading-text="Loading visits…">
      <template #table>
        <v-card class="vms-surface" elevation="0">
          <v-data-table
            :headers="computedHeaders" 
            :items="filteredVisits" 
            sort-by="name" 
            class="elevation-0"
            :loading="loading"
            loading-text="Loading visits…" 
            :search="filterReportBySearch" 
            show-select 
            :single-select="singleSelect"
            v-model="selected"
            :server-items-length="visitsPagination?.total"
            :options.sync="options"
          >
            <template v-slot:[`item.id`]="{ index }">
              {{ ++index }}
            </template>

            <template v-slot:[`item.event_date`]="{ item }">
              {{ formatDate(item.event_date) }}
            </template>

            <template v-slot:[`item.time`]="{ item }">
              {{ item.start_at }} - {{ item.end_at }}
            </template>

            <template v-slot:[`item.title`]="{ item }">
              <a
                @click.stop="visitPreview(item)"
                class="info--text text--darken-1 text-decoration-underline"
                >{{ item.title }}</a
              >
            </template>
            
            <template v-slot:[`item.status`]="{ item }">
              <div v-if="workflowSteps(item).length > 0">
                <span v-if="item.receiving == 0" class="vms-status vms-status--closed">Closed</span>
                <span v-else-if="item.active == 0" class="vms-status vms-status--rejected">Rejected</span>
                <span
                  v-else-if="item.active == 1 && workflowSteps(item).length > item.status"
                  class="vms-status"
                  :class="item.current_user ? 'vms-status--mine' : 'vms-status--pending'"
                  style="cursor: pointer"
                  @click.stop="visitPreview(item)"
                >
                  {{ item.current_user ? 'Your turn — open to Approve/Reject' : 'Pending' }}
                  · {{ workflowStepTitle(item, item.status) }}
                </span>
                <span
                  v-else-if="workflowSteps(item).length == item.status && item.active == 1"
                  class="vms-status vms-status--approved"
                >
                  Approved
                </span>
                <span v-else-if="item.active == 2" class="vms-status vms-status--pending">Pending · HOD</span>
                <span v-else class="vms-status vms-status--closed">Undefined</span>
              </div>
              <div v-else>
                <router-link class="red--text text-decoration-none" to="/workflows">
                  Please select workflow steps
                </router-link>
              </div>
            </template>

            <template v-slot:[`item.workflow`]="{ item }">
              <div v-if="workflowSteps(item).length > 0">
                <a
                  v-for="(wkfl, i) in workflowSteps(item)"
                  :key="`visit-wf-${item.vst_id || item.id}-${wkfl.id || wkfl.role_key || wkfl.title}-${i}`"
                  class="success--text"
                >
                  <span v-if="i != 0"> <v-icon>mdi-arrow-right-bold</v-icon> </span>
                  <a
                    v-if="i == item.status && item.active == 1"
                    class="success--text"
                    >{{ wkfl.title || wkfl.label }}</a
                  >
                  <a v-else class="primary--text">{{ wkfl.title || wkfl.label }}</a>
                </a>
              </div>

              <div v-else>
                <router-link class="red--text text-decoration-none" to="/workflows">
                  Please Select Workflow Steps
                </router-link>
              </div>
            </template>

            <template v-slot:[`item.actions`]="{ item }">
              <div class="vms-table-actions">
                <template v-if="isAwaitingMe(item)">
                  <v-btn
                    small
                    color="success"
                    dark
                    elevation="0"
                    class="text-capitalize mr-1"
                    @click.stop="visitPreview(item)"
                  >
                    <v-icon left small>mdi-check-circle-outline</v-icon>
                    Approve
                  </v-btn>
                  <v-btn
                    small
                    color="error"
                    outlined
                    elevation="0"
                    class="text-capitalize mr-1"
                    @click.stop="visitPreview(item)"
                  >
                    <v-icon left small>mdi-close-circle-outline</v-icon>
                    Reject
                  </v-btn>
                </template>
                <VmsIconAction
                  v-else
                  icon="mdi-eye"
                  label="Preview visit"
                  color="grey"
                  @click="editItem(item)"
                />
                <VmsIconAction
                  icon="mdi-link-variant"
                  label="Copy invite link"
                  color="info"
                  @click="copyInviteLink(item)"
                />
                <VmsIconAction
                  icon="mdi-badge-account-horizontal"
                  label="Print badge"
                  color="primary"
                  @click="printBadge(item)"
                />
                <VmsIconAction
                  v-if="!showUpcomingVisits"
                  icon="mdi-text-box-outline"
                  label="Visit summary"
                  color="info"
                  @click="loadPostSummary(item)"
                />
                <v-chip
                  v-if="riskChip(item)"
                  x-small
                  class="ml-1"
                  :color="riskChip(item).color"
                  dark
                  @click="loadRisk(item)"
                >
                  {{ riskChip(item).label }}
                </v-chip>
              </div>
            </template>

            <template v-slot:no-data>
              <div class="vms-empty">
                <v-icon large color="grey lighten-1">mdi-calendar-blank-outline</v-icon>
                <p class="mt-3 mb-1" style="font-size: 1.05rem; color: #1a1a1a; font-weight: 600;">
                  {{ emptyTitle }}
                </p>
                <p class="mb-4">{{ emptySubtitle }}</p>
                <v-btn v-if="statusFilter !== 'all'" text color="info" class="mr-2" @click="applyStatusFilter('all')">
                  Clear filter
                </v-btn>
                <v-btn color="info" dark @click="addVisit">
                  <v-icon left>mdi-calendar-plus</v-icon>
                  Invite visitor
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </template>

      <template #card="{ item }">
        <div class="vms-list-card__title">
          <a
            @click.stop="visitPreview(item)"
            class="info--text text--darken-1 text-decoration-underline"
            >{{ item.title || 'Untitled visit' }}</a
          >
        </div>
        <div class="vms-list-card__meta">
          <span v-if="item.visitor?.name"><v-icon x-small left>mdi-account</v-icon>{{ item.visitor.name }}</span>
          <span v-if="item.host?.name"><v-icon x-small left>mdi-account-tie</v-icon>{{ item.host.name }}</span>
        </div>
        <div class="vms-list-card__row">
          <span class="vms-list-card__label">Date</span>
          <span>{{ formatDate(item.event_date) }}</span>
        </div>
        <div class="vms-list-card__row">
          <span class="vms-list-card__label">Time</span>
          <span>{{ item.start_at }} - {{ item.end_at }}</span>
        </div>
        <div class="vms-list-card__row" v-if="item.facility">
          <span class="vms-list-card__label">Location</span>
          <span>{{ item.facility }}</span>
        </div>
        <div class="vms-list-card__row">
          <span class="vms-list-card__label">Status</span>
          <span>
            <template v-if="workflowSteps(item).length > 0">
              <span v-if="item.receiving == 0" class="vms-status vms-status--closed">Closed</span>
              <span v-else-if="item.active == 0" class="vms-status vms-status--rejected">Rejected</span>
              <span
                v-else-if="item.active == 1 && workflowSteps(item).length > item.status"
                class="vms-status"
                :class="item.current_user ? 'vms-status--mine' : 'vms-status--pending'"
                @click.stop="visitPreview(item)"
              >
                {{ item.current_user ? 'Your turn — open to Approve/Reject' : 'Pending' }}
                · {{ workflowStepTitle(item, item.status) }}
              </span>
              <span
                v-else-if="workflowSteps(item).length == item.status && item.active == 1"
                class="vms-status vms-status--approved"
              >
                Approved
              </span>
              <span v-else-if="item.active == 2" class="vms-status vms-status--pending">Pending · HOD</span>
              <span v-else class="vms-status vms-status--closed">Undefined</span>
            </template>
            <router-link v-else class="red--text text-decoration-none" to="/workflows">
              Please select workflow steps
            </router-link>
          </span>
        </div>
        <div class="vms-list-card__actions">
          <template v-if="isAwaitingMe(item)">
            <v-btn
              small
              color="success"
              dark
              elevation="0"
              class="text-capitalize"
              @click.stop="visitPreview(item)"
            >
              <v-icon left small>mdi-check-circle-outline</v-icon>
              Approve
            </v-btn>
            <v-btn
              small
              color="error"
              outlined
              elevation="0"
              class="text-capitalize"
              @click.stop="visitPreview(item)"
            >
              <v-icon left small>mdi-close-circle-outline</v-icon>
              Reject
            </v-btn>
          </template>
          <VmsIconAction
            v-else
            icon="mdi-eye"
            label="Preview"
            color="grey"
            @click="editItem(item)"
          />
          <VmsIconAction
            icon="mdi-link-variant"
            label="Copy invite link"
            color="info"
            @click="copyInviteLink(item)"
          />
          <VmsIconAction
            icon="mdi-badge-account-horizontal"
            label="Print badge"
            color="primary"
            @click="printBadge(item)"
          />
          <VmsIconAction
            v-if="!showUpcomingVisits"
            icon="mdi-text-box-outline"
            label="Visit summary"
            color="info"
            @click="loadPostSummary(item)"
          />
        </div>
      </template>

      <template #empty>
        <div class="vms-empty">
          <v-icon large color="grey lighten-1">mdi-calendar-blank-outline</v-icon>
          <p class="mt-3 mb-1" style="font-size: 1.05rem; color: #1a1a1a; font-weight: 600;">
            {{ emptyTitle }}
          </p>
          <p class="mb-4">{{ emptySubtitle }}</p>
          <v-btn v-if="statusFilter !== 'all'" text color="info" class="mr-2" @click="applyStatusFilter('all')">
            Clear filter
          </v-btn>
          <v-btn color="info" dark @click="addVisit">
            <v-icon left>mdi-calendar-plus</v-icon>
            Invite visitor
          </v-btn>
        </div>
      </template>
    </VmsResponsiveList>

    <!-- Schedule Visit Modal -->
    <ScheduleVisitModal 
      v-model="showScheduleModal"
      :organization-facilities="organizationFacilities"
      :approval-workflows="approvalWorkflows"
      :organization="organization"
      @get_upcoming_visits="getUpcomingVisits"
      @select_organization="selectOrganization"
      :selected-visit="selectedVisit"
    />
  </div>
</template>
<script>
import organizationService from '../../services/organization.service';
import ReportsService from '../../services/reports.service';
import moment from 'moment';
import ScheduleVisitModal from '../../components/ScheduleVisitModal.vue';
import WorkflowService from '../../services/workflow.service';
import VmsPageHeader from '../../components/VmsPageHeader.vue';
import VmsResponsiveList from '../../components/VmsResponsiveList.vue';
import VmsIconAction from '../../components/VmsIconAction.vue';
import VmsService from '../../services/vms.service';

export default {
  name: "ScheduleVisit",

  components: {
    ScheduleVisitModal,
    VmsPageHeader,
    VmsResponsiveList,
    VmsIconAction,
  },

  props: [
    'organization'
  ],

  data() {
    return {
      visits: [],
      visitsPagination: {},
      selected: [],
      singleSelect: false,
      filterReportBySearch: null,
      options: {},
      loading: false,
      selectedHeaders: [],
      visitHeaders: [
        {
          text: "#",
          value: "id",
          visible: true,
        }, 
        {
          text: "Visitor",
          align: "start",
          filterable: true,
          value: "visitor.name",
          visible: true,
        },
        {
          text: "Host",
          value: "host.name",
          filterable: false,
          visible: true,
        },
        {
          text: "Title",
          value: "title",
          filterable: false,
          visible: false,
        },
        {
          text: "Approval Stages",
          value: "workflow",
          filterable: false,
          visible: false,
        },
        {
          text: "Approval Status",
          value: "status",
          filterable: false,
          visible: true,
        },
        {
          text: "Purpose / notes",
          value: "notes",
          filterable: false,
          visible: false,
        },
        {
          text: "Location",
          value: "facility",
          filterable: false,
          visible: true,
        },
        {
          text: "Date",
          align: "center",
          value: "event_date",
          filterable: false,
          visible: true,
        },
        {
          text: "Time",
          align: "center",
          value: "time",
          filterable: false,
          visible: false,
        },
        {
          text: "Visit Status",
          value: "attend_status",
          filterable: false,
          visible: false,
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          visible: true,
          align: "end",
          width: "220px",
          class: "vms-col-actions text-end",
          cellClass: "vms-col-actions text-end",
        },
      ],
      showUpcomingVisits: true,
      listMode: 'upcoming',
      statusFilter: 'all',
      showScheduleModal: false,
      apiErrors: [],
      visitCategories: ["Official", "Personal"],
      closeOnContentClick: false,
      dateToday: (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10),
      national_number: "KE",
      phoneNumber: null,
      phoneValidationError: null,
      organizationFacilities: [],
      approvalWorkflows: [],
      valid: false,
      selectedVisit: null,
      riskCache: {},
      smartSearchLoading: false,
      smartSearchHint: '',
      snapshotCounts: {
        all: 0,
        mine: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
      },
    }
  },

  computed: {
    visitsPageTitle() {
      return this.showUpcomingVisits ? "Today's visits" : "Past visits";
    },
    organizationFacility() {
      return this.organization?.facility?.[0] ?? { 'id': null, 'facility': null };
    },

    visitsWithIndex() {
      if (!(this.visits || []).length) {
        return [];
      }

      return this.visits.map(
        (visits, index) => ({
          ...visits,
          index: index + 1
        })
      );
    },

    computedHeaders() {
      let headers = this.visitHeaders.filter((item) => (item.visible == true));
      return headers;
    },

    snapshot() {
      return this.snapshotCounts;
    },

    filteredVisits() {
      // Server already applies status_filter — use the loaded page as-is.
      return this.visits || [];
    },

    emptyTitle() {
      if (this.statusFilter === 'mine') return 'Nothing waiting for you';
      if (this.statusFilter === 'pending') return 'No pending visits';
      if (this.statusFilter === 'approved') return 'No approved visits yet';
      if (this.statusFilter === 'rejected') return 'No rejected visits';
      return 'No visits yet';
    },

    emptySubtitle() {
      if (this.statusFilter !== 'all') {
        return 'Try another filter, or invite a guest.';
      }
      return 'Invite a guest — location and workflow fill in automatically.';
    },
  },

  created() {
    this.$nextTick(() => {
      this.selectedHeaders = this.computedHeaders;
      this.getUpcomingVisits();
      this.getPastVisits();
      this.getOrganizationFacilities();
      this.getApprovalWorkflows();
      this.showScheduleModal = false;
      this.selectedVisit = null;
    });
  },

  methods: {
    async selectOrganization(payload) {
      const keepInviteOpen = !!payload?.keepInviteOpen || this.showScheduleModal;
      const orgId = payload?.selectedOrganization?.id || this.organization?.id;
      await this.$emit('select_organization', payload);
      await this.$nextTick();

      this.getOrganizationFacilities();
      this.getUpcomingVisits();
      this.getPastVisits();
      await this.getApprovalWorkflows(orgId);

      // Changing branch from the invite form should keep the modal open and refresh workflow
      if (!keepInviteOpen) {
        this.showScheduleModal = false;
        this.selectedVisit = null;
      }
    },
    
    toggleColumnVisibility() {
      if (this.selectedHeaders && this.selectedHeaders.length > 0) {
        this.visitHeaders.forEach((visitHeader) => {
          let index = -1;
          index = this.visitHeaders.findIndex((header) => header.value == visitHeader.value);
          if (index > -1) {
            this.visitHeaders[index].visible = false;
          }
        });

        this.selectedHeaders.forEach((selectedHeader) => {
          let index = -1;
          index = this.visitHeaders.findIndex((header) => header.value == selectedHeader.value);
          if (index > -1) {
            this.visitHeaders[index].visible = true;
          }
        });

        // Actions column must always stay visible for Approve/Reject.
        const actionsIndex = this.visitHeaders.findIndex((header) => header.value === 'actions');
        if (actionsIndex > -1) {
          this.visitHeaders[actionsIndex].visible = true;
          if (!this.selectedHeaders.some((h) => h.value === 'actions')) {
            this.selectedHeaders.push(this.visitHeaders[actionsIndex]);
          }
        }
      }
    },

    addVisit() {
      this.$nextTick(() => {
        this.selectedVisit = null;
        this.$refs.form?.clear();
        this.scheduleModal();
      });
    },

    scheduleModal() {
      this.showScheduleModal = true;
    },

    formatDate(date, format = 'DD-MMM-YYYY') {
      return moment(date).format(format);
    },

    workflowSteps(item) {
      if (Array.isArray(item?.approval_path) && item.approval_path.length) {
        return item.approval_path;
      }
      const wf = item?.workflow || {};
      if (Array.isArray(wf.approval_groups) && wf.approval_groups.length) {
        return wf.approval_groups;
      }
      if (Array.isArray(wf.steps) && wf.steps.length) {
        return wf.steps;
      }
      return [];
    },

    workflowStepTitle(item, index) {
      const step = this.workflowSteps(item)[Number(index) || 0];
      return step?.title || step?.label || step?.role_key || 'Step';
    },

    isPending(item) {
      return Number(item?.active) === 1
        && Number(item?.execute) !== 1
        && this.workflowSteps(item).length > Number(item?.status || 0);
    },

    isApproved(item) {
      return Number(item?.active) === 1
        && (Number(item?.execute) === 1
          || this.workflowSteps(item).length === Number(item?.status || 0));
    },

    isAwaitingMe(item) {
      return this.isPending(item) && (item.current_user === true || item.current_user === 1);
    },

    toggleVisits() {
      let visits = [];
      let visitsPagination = {};
      
      if (this.showUpcomingVisits) {
        visitsPagination = this.upcomingVisits ?? {};
        visits = this.upcomingVisits?.data ?? [];
      } else {
        visitsPagination = this.pastVisits ?? {};
        visits = this.pastVisits?.data ?? [];
      }
      
      this.visits = visits;
      this.visitsPagination = visitsPagination;
      this.prefetchRisks(visits);
    },

    prefetchRisks(visits) {
      (visits || []).slice(0, 15).forEach((item) => {
        const visitId = item.vst_id || item.id;
        if (!visitId || this.riskCache[visitId]) return;
        VmsService.visitRisk(visitId).then((res) => {
          const data = res?.data?.data ?? res?.data ?? {};
          this.$set(this.riskCache, visitId, data);
        }).catch(() => {});
      });
    },

    showUpcomingVisit() {
      this.listMode = 'upcoming';
      this.statusFilter = 'all';
      this.getUpcomingVisits();
      this.showUpcomingVisits = true;
      this.toggleVisits();
    },

    showPastVisit() {
      this.listMode = 'past';
      this.statusFilter = 'all';
      this.getPastVisits();
      this.showUpcomingVisits = false;
      this.toggleVisits();
    },

    applyStatusFilter(filter) {
      const next = filter || 'all';
      const needsPageReset = Number(this.options?.page || 1) !== 1;
      this.statusFilter = next;
      if (needsPageReset) {
        // options watcher will refetch
        this.options = {
          ...this.options,
          page: 1,
        };
        return;
      }
      if (this.showUpcomingVisits) {
        this.getUpcomingVisits();
      } else {
        this.getPastVisits();
      }
    },

    async runSmartSearch() {
      const query = (this.filterReportBySearch || '').trim();
      if (!query) {
        this.$store.dispatch('alert/error', 'Type a phrase like “pending Kisumu tomorrow”.');
        return;
      }
      this.smartSearchLoading = true;
      try {
        const response = await VmsService.parseVisitSearch({
          organization_id: this.organization?.id,
          query,
        });
        const data = response?.data?.data ?? response?.data ?? {};
        this.filterReportBySearch = data.search || query;
        this.smartSearchHint = data.hint || '';

        if (data.list === 'past' && this.showUpcomingVisits) {
          this.showPastVisit();
        } else if (data.list !== 'past' && !this.showUpcomingVisits) {
          this.showUpcomingVisit();
        }

        if (data.status_filter) {
          this.applyStatusFilter(data.status_filter);
        } else if (this.showUpcomingVisits) {
          this.getUpcomingVisits();
        } else {
          this.getPastVisits();
        }
      } catch (e) {
        this.$store.dispatch('alert/error', e.response?.data?.message || 'Smart search failed');
      } finally {
        this.smartSearchLoading = false;
      }
    },

    loadPostSummary(item) {
      const visitId = item.vst_id || item.id;
      if (!visitId) return;
      VmsService.postVisitSummary(visitId).then((res) => {
        const data = res?.data?.data ?? res?.data ?? {};
        this.$store.dispatch('alert/info', data.summary || 'Summary ready');
      }).catch((e) => {
        this.$store.dispatch('alert/error', e.response?.data?.message || 'Could not load summary');
      });
    },

    updateSnapshotCounts(payload) {
      const counts = payload?.counts || {};
      this.snapshotCounts = {
        all: counts.all ?? 0,
        mine: counts.mine ?? 0,
        pending: counts.pending ?? 0,
        approved: counts.approved ?? 0,
        rejected: counts.rejected ?? 0,
      };
    },

    getUpcomingVisits() {
      if (!this.organization?.id) {
        this.upcomingVisits = { data: [], total: 0 };
        this.updateSnapshotCounts({});
        this.toggleVisits();
        this.loading = false;
        return;
      }
      this.loading = true;
      ReportsService.upcomingVisits(this.organization.id, {
        options: this.options,
        status_filter: this.statusFilter || 'all',
      }).then(
        success => {
          this.upcomingVisits = success.data ? success.data.data : this.upcomingVisits;
          this.updateSnapshotCounts(this.upcomingVisits);
          this.toggleVisits();
          this.loading = false;
        },
        error => {
          console.log(error)
          this.upcomingVisits = { data: [], total: 0 };
          this.updateSnapshotCounts({});
          this.toggleVisits();
          this.loading = false;
        }
      )
    },

    getPastVisits() {
      if (!this.organization?.id) {
        this.pastVisits = { data: [], total: 0 };
        this.toggleVisits();
        this.loading = false;
        return;
      }
      this.loading = true;
      ReportsService.pastVisits(this.organization.id, {
        options: this.options,
        status_filter: this.showUpcomingVisits ? 'all' : (this.statusFilter || 'all'),
      }).then(
        success => {
          this.pastVisits = success.data.data;
          if (!this.showUpcomingVisits) {
            this.updateSnapshotCounts(this.pastVisits);
          }
          this.toggleVisits();
          this.loading = false;
        },
        error => {
          console.log(error)
          this.pastVisits = { data: [], total: 0 };
          this.toggleVisits();
          this.loading = false;
        }
      )
    },

    getOrganizationFacilities() {
      organizationService.organization_facilities(this.organization.id).then(
        success => {
          this.organizationFacilities = success.data ? success.data.data : [];
        },
        error => {
          console.log(error)
          this.organizationFacilities = [];
        }
      )
    },

    getApprovalWorkflows(organizationId = null) {
      const orgId = organizationId || this.organization?.id;
      if (!orgId) {
        this.approvalWorkflows = [];
        return Promise.resolve([]);
      }

      return WorkflowService.getApprovalWorkflows(orgId).then(
        success => {
          this.approvalWorkflows = success.data ? success.data?.data?.data : [];
          return this.approvalWorkflows;
        },
        error => {
          console.log(error)
          this.approvalWorkflows = [];
          return [];
        }
      )
    },

    visitPreview(item) {
      if (!this.isAwaitingMe(item) && !item.current_user) {
        this.$store.dispatch("alert/error", "You are not allowed to act on this Visit");
        return;
      }

      this.$nextTick(() => {
        // Report rows expose visit id as vst_id (events.* can overwrite id).
        this.selectedVisit = Object.assign({}, item, {
          id: item.vst_id || item.id,
          notes: item.notes || item.purpose_notes || '',
          assets: item.assets || '',
        });
        this.scheduleModal();
      });
    },

    editItem(item) {
      this.visitPreview(item);
    },

    copyInviteLink(item) {
      let url = VmsService.ensureInviteLink(item);
      if (url) {
        this.writeClipboard(url);
        return;
      }
      const visitId = item.vst_id || item.id;
      VmsService.openBadgeJson(visitId).then((inviteUrl) => {
        if (inviteUrl) {
          item.guest_token = item.guest_token || inviteUrl.split('/guest/')[1];
          this.writeClipboard(inviteUrl);
        } else {
          this.$store.dispatch('alert/error', 'Invite link not ready yet');
        }
      }).catch((error) => {
        this.$store.dispatch('alert/error', error.response?.data?.message || 'Could not create invite link');
      });
    },

    writeClipboard(url) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(
          () => this.$store.dispatch('alert/success', 'Invite link copied'),
          () => this.$store.dispatch('alert/error', 'Could not copy link')
        );
      } else {
        window.prompt('Copy invite link', url);
      }
    },

    printBadge(item) {
      const visitId = item.vst_id || item.id;
      if (!visitId) {
        this.$store.dispatch('alert/error', 'Visit id missing');
        return;
      }
      VmsService.openBadge(visitId).catch((error) => {
        this.$store.dispatch('alert/error', error.response?.data?.message || 'Could not open badge');
      });
    },

    riskChip(item) {
      const risk = item._risk || this.riskCache[item.vst_id || item.id];
      if (!risk || risk.level === 'low') return null;
      return {
        label: risk.level === 'high' ? 'High risk' : 'Risk',
        color: risk.level === 'high' ? 'error' : 'warning',
      };
    },

    loadRisk(item) {
      const visitId = item.vst_id || item.id;
      VmsService.visitRisk(visitId).then((res) => {
        const data = res?.data?.data ?? res?.data ?? {};
        this.$set(this.riskCache, visitId, data);
        this.$store.dispatch('alert/info', data.summary || 'Risk assessed');
      }).catch(() => {});
    },
  },
  watch: {
    options: {
      handler () {
        if (this.showUpcomingVisits) {
          this.getUpcomingVisits();
        } else {
          this.getPastVisits();
        }
      },
      deep: true,
    },
  },
}
</script>